import React, { useEffect, useState } from "react";
import { workforceApi } from "../../lib/workforce";
import BrainBadge from "../../components/workforce/BrainBadge";
import { DollarSign, Users, MessageSquare, TrendingUp } from "lucide-react";

export default function WorkforceAnalytics() {
  const [data, setData] = useState(null);
  const [brainMap, setBrainMap] = useState({});
  useEffect(() => {
    Promise.all([workforceApi.analyticsSummary(), workforceApi.listBrains()]).then(([d, b]) => {
      setData(d);
      const m = {}; (b.brains || []).forEach((x) => { m[x.id] = x; });
      setBrainMap(m);
    });
  }, []);
  if (!data) return <div className="p-8 text-center text-slate-400 italic">Crunching numbers…</div>;
  const maxCost = Math.max(...data.brain_usage.map((b) => b.cost), 0.0001);

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="analytics-page">
      <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Workforce Analytics</div>
      <h1 className="font-heading text-3xl md:text-4xl font-bold mb-6">How's your team doing?</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat icon={Users} label="Workers"       value={data.total_workers}                       color="#3B82F6" />
        <Stat icon={MessageSquare} label="Assistant messages" value={data.total_messages}         color="#10B981" />
        <Stat icon={DollarSign} label="Total spend"  value={`$${data.total_cost.toFixed(4)}`}    color="#F59E0B" />
        <Stat icon={TrendingUp} label="Brains in use" value={data.brain_usage.length}            color="#8B5CF6" />
      </div>

      <div className="brut-card p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-xl font-bold">Cost by brain</h2>
        </div>
        {data.brain_usage.length === 0 ? (
          <div className="text-sm text-slate-500 italic py-6 text-center">No usage yet.</div>
        ) : (
          <div className="space-y-2" data-testid="brain-usage-list">
            {data.brain_usage.sort((a, b) => b.cost - a.cost).map((b) => (
              <div key={b.brain_id} className="flex items-center gap-3">
                <div className="w-32 shrink-0">
                  <BrainBadge brain={brainMap[b.brain_id] || { name: b.brain_name, color: "#64748B", family: b.family }} size="sm" />
                </div>
                <div className="flex-1 h-6 rounded-full bg-slate-100 overflow-hidden border-2 border-slate-900">
                  <div
                    className="h-full flex items-center justify-end pr-2 text-xs font-mono font-bold text-white"
                    style={{ width: `${(b.cost / maxCost) * 100}%`, backgroundColor: (brainMap[b.brain_id]?.color) || "#64748B" }}
                  >
                    ${b.cost.toFixed(4)}
                  </div>
                </div>
                <div className="w-20 text-right text-xs font-bold text-slate-500">{b.messages} msg</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="brut-card p-5">
        <h2 className="font-heading text-xl font-bold mb-3">Workers</h2>
        {data.workers.length === 0 ? (
          <div className="text-sm text-slate-500 italic py-6 text-center">No workers yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 font-bold border-b-2 border-slate-900">
                <th className="py-2">Worker</th><th>Title</th><th>Brain</th><th className="text-right">Tasks</th><th className="text-right">Spend</th>
              </tr>
            </thead>
            <tbody>
              {data.workers.map((w) => (
                <tr key={w.worker_id} className="border-b border-slate-100">
                  <td className="py-2"><span className="mr-2">{w.avatar}</span><span className="font-bold">{w.name}</span></td>
                  <td className="text-slate-600">{w.title}</td>
                  <td>{brainMap[w.brain_id] ? <BrainBadge brain={brainMap[w.brain_id]} size="sm" /> : <span className="text-slate-400">—</span>}</td>
                  <td className="text-right font-mono">{w.tasks_completed}</td>
                  <td className="text-right font-mono font-bold">${w.cost_total.toFixed(4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: I, label, value, color }) {
  return (
    <div className="brut-card p-4 flex items-center gap-3">
      <div className="w-11 h-11 rounded-xl border-2 flex items-center justify-center" style={{ backgroundColor: color + "1F", borderColor: color, color }}>
        <I size={18} strokeWidth={2.5} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{label}</div>
        <div className="font-heading font-bold text-xl leading-none">{value}</div>
      </div>
    </div>
  );
}
